/**
 * Os colaboradores das casas, em tabela.
 *
 * Papel: mostra uma linha por pessoa, com o nome, o cargo, o CPF, a casa onde
 * trabalha, a admissão, o salário e a situação — e, na tabela dos desligados,
 * também a data de saída.
 *
 * Como usar: `variante` escolhe o rótulo da coluna de salário
 * (`'ativos'` → "Salário base", `'desligados'` → "Último salário") e se a
 * coluna de desligamento aparece; quem monta a tela passa a lista já
 * filtrada por situação, numa tabela para cada uma — a mesma pessoa nunca
 * aparece nas duas ao mesmo tempo. `onEditar` e `onRemover` valem para as
 * duas variantes, e só quem tem nível `editar` os recebe; sem eles a coluna
 * de ações some inteira.
 *
 * `selecao` acrescenta, antes do nome, a coluna das caixas de marcar — uma por
 * linha, com o título "Selecionar" e o nome da pessoa, e uma no cabeçalho,
 * "Selecionar todos", que marca ou desmarca todas as linhas desta tabela. Só a
 * tabela da equipe ativa a recebe: quem saiu não entra na exportação, e a
 * tabela dos desligados não tem a coluna. A marcação é guardada pelo `id` da
 * pessoa, e não pela posição da linha, e por isso continua valendo quando a
 * lista volta da API em outra ordem. A coluna some na impressão
 * (`print:hidden`), como a barra que exporta.
 *
 * As quatro colunas de benefício (ajuda de custo, ajuda médica, alimentação e
 * transporte) não aparecem: nenhum colaborador tem valor preenchido nelas, e o
 * cadastro não guarda o campo — trazer quatro colunas sempre vazias não
 * ajudaria quem lê a tabela.
 *
 * Cada cabeçalho, menos o das caixas de marcar e o das ações, ordena a tabela
 * pela regra `'com-retorno'` de `useOrdenacao`: crescente, decrescente e de
 * volta à ordem em que `colaboradores` chegou, com o vazio sempre no fim.
 * Admissão e desligamento ordenam como data, o salário como número e o resto
 * como texto — o nome pelo `nomeCompleto`, o cargo sem o departamento, o CPF e
 * o status pelo que a célula escreve. Cada tabela montada guarda a própria
 * ordenação: ordenar a equipe ativa não mexe na dos desligados.
 */
import { type Colaborador } from '@concierge/shared';

import {
  CabecalhoOrdenavel,
  type ColunaOrdenavel,
  SETA_EM_TEXTO,
  useOrdenacao,
} from '@/shared/embutido';
import { formatarCpf, formatarData, formatarDinheiro } from '@/shared/lib/formato';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/ui/table';
import { ROTULO_DO_STATUS } from '../rotulos';

/** A marcação das linhas: quem está marcado e o que fazer a cada clique. */
export interface SelecaoDeLinhas {
  /** Os `id` das pessoas marcadas. */
  marcados: ReadonlySet<string>;
  /** Chamado com a pessoa da linha e o novo estado da caixa dela. */
  onAlternar: (colaborador: Colaborador, marcado: boolean) => void;
  /** Chamado pela caixa do cabeçalho, com o novo estado dela. */
  onAlternarTodos: (marcado: boolean) => void;
}

interface TabelaColaboradoresProps {
  colaboradores: Colaborador[];
  /** Rótulo da coluna de salário e presença da coluna de desligamento. */
  variante: 'ativos' | 'desligados';
  /** Chamado com o colaborador da linha; ausente esconde a coluna de ações. */
  onEditar?: (colaborador: Colaborador) => void;
  /** Chamado com o colaborador a excluir; a confirmação é de quem monta. */
  onRemover?: (colaborador: Colaborador) => void;
  /** A marcação das linhas; ausente tira a coluna das caixas de marcar. */
  selecao?: SelecaoDeLinhas;
}

/**
 * A variante de `Badge` de cada situação.
 *
 * `ativo` é a situação resolvida e `desligado` é a encerrada sem seguir — as
 * duas pontas opostas do vínculo. `diarista`, sem vínculo formal contínuo,
 * fica em `pendente` por não ser nenhuma das duas.
 */
const TOM_DO_STATUS: Record<Colaborador['status'], 'pendente' | 'concluido' | 'vencido'> = {
  ativo: 'concluido',
  desligado: 'vencido',
  diarista: 'pendente',
};

/** As colunas ordenáveis; `desligamento` só aparece na variante `'desligados'`. */
const COLUNAS: Record<string, ColunaOrdenavel<Colaborador>> = {
  nome: { tipo: 'texto', valor: (c) => c.nomeCompleto },
  cargo: { tipo: 'texto', valor: (c) => c.cargo },
  cpf: { tipo: 'texto', valor: (c) => formatarCpf(c.cpf) },
  residencia: { tipo: 'texto', valor: (c) => c.residencia.apelido },
  admissao: { tipo: 'data', valor: (c) => c.admissao },
  salario: { tipo: 'numero', valor: (c) => c.salario },
  status: { tipo: 'texto', valor: (c) => ROTULO_DO_STATUS[c.status] },
  desligamento: { tipo: 'data', valor: (c) => c.desligamento },
};

/**
 * A tabela de colaboradores.
 *
 * @param colaboradores - As pessoas a mostrar, já filtradas pela situação que
 *   `variante` descreve, na ordem que vale antes de qualquer clique e à qual o
 *   terceiro clique devolve.
 * @param variante - `'ativos'` rotula o salário como "Salário base" e omite a
 *   coluna de desligamento; `'desligados'` rotula como "Último salário" e
 *   acrescenta a coluna.
 * @param onEditar - Chamado ao clicar em editar; ausente esconde as ações.
 * @param onRemover - Chamado ao clicar em remover; ausente esconde as ações.
 * @param selecao - Quem está marcado e o que fazer a cada clique nas caixas;
 *   ausente tira a coluna. A caixa do cabeçalho aparece marcada só quando todas
 *   as linhas desta tabela estão.
 * @returns A tabela com uma linha por pessoa.
 */
export function TabelaColaboradores({
  colaboradores,
  variante,
  onEditar,
  onRemover,
  selecao,
}: TabelaColaboradoresProps) {
  const mostrarAcoes = onEditar !== undefined || onRemover !== undefined;
  const mostrarDesligamento = variante === 'desligados';
  const ordenacao = useOrdenacao(colaboradores, COLUNAS, 'com-retorno');
  const cabecalhos: [chave: string, rotulo: string][] = [
    ['nome', 'Nome'],
    ['cargo', 'Cargo'],
    ['cpf', 'CPF'],
    ['residencia', 'Residência'],
    ['admissao', 'Admissão'],
    ['salario', mostrarDesligamento ? 'Último salário' : 'Salário base'],
    ['status', 'Status'],
  ];
  if (mostrarDesligamento) cabecalhos.push(['desligamento', 'Desligamento']);
  const todosMarcados =
    selecao !== undefined &&
    colaboradores.length > 0 &&
    colaboradores.every((colaborador) => selecao.marcados.has(colaborador.id));

  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            {selecao !== undefined && (
              <TableHead className="w-[34px] text-center print:hidden">
                <input
                  type="checkbox"
                  title="Selecionar todos"
                  checked={todosMarcados}
                  onChange={(evento) => selecao.onAlternarTodos(evento.target.checked)}
                  className="size-4 cursor-pointer accent-brass"
                />
              </TableHead>
            )}
            {cabecalhos.map(([chave, rotulo]) => (
              <CabecalhoOrdenavel
                key={chave}
                como={TableHead}
                title={`Ordenar por ${rotulo}`}
                seta={ordenacao.seta(chave)}
                classeDaSeta={SETA_EM_TEXTO}
                onAlternar={() => ordenacao.alternar(chave)}
              >
                {rotulo}
              </CabecalhoOrdenavel>
            ))}
            {mostrarAcoes && (
              <TableHead>
                <span className="sr-only">Ações</span>
              </TableHead>
            )}
          </TableRow>
        </TableHeader>
        <TableBody>
          {ordenacao.linhas.map((colaborador) => (
            <TableRow key={colaborador.id}>
              {selecao !== undefined && (
                <TableCell className="w-[34px] text-center print:hidden">
                  <input
                    type="checkbox"
                    title={`Selecionar ${colaborador.nomeCompleto}`}
                    checked={selecao.marcados.has(colaborador.id)}
                    onChange={(evento) =>
                      selecao.onAlternar(colaborador, evento.target.checked)
                    }
                    className="size-4 cursor-pointer accent-brass"
                  />
                </TableCell>
              )}
              <TableCell className="text-texto-medio">
                <span className="font-medium text-foreground">{colaborador.nomeCompleto}</span>
                {colaborador.apelido !== null && (
                  <span className="block text-xs text-texto-fraco">{colaborador.apelido}</span>
                )}
              </TableCell>
              <TableCell>
                {colaborador.cargo}
                {colaborador.departamento !== null && (
                  <span className="block text-xs text-texto-fraco">
                    {colaborador.departamento}
                  </span>
                )}
              </TableCell>
              <TableCell className="tabular">{formatarCpf(colaborador.cpf)}</TableCell>
              <TableCell>{colaborador.residencia.apelido}</TableCell>
              <TableCell className="tabular">
                {colaborador.admissao === null ? '—' : formatarData(colaborador.admissao)}
              </TableCell>
              <TableCell className="tabular">
                {colaborador.salario === null ? '—' : formatarDinheiro(colaborador.salario)}
              </TableCell>
              <TableCell>
                <Badge variant={TOM_DO_STATUS[colaborador.status]} className="w-fit">
                  {ROTULO_DO_STATUS[colaborador.status]}
                </Badge>
              </TableCell>
              {mostrarDesligamento && (
                <TableCell className="tabular">
                  {colaborador.desligamento === null
                    ? '—'
                    : `Desligado em ${formatarData(colaborador.desligamento)}`}
                </TableCell>
              )}
              {mostrarAcoes && (
                <TableCell>
                  <div className="flex gap-2">
                    {onEditar !== undefined && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onEditar(colaborador)}
                      >
                        editar
                      </Button>
                    )}
                    {onRemover !== undefined && (
                      <Button variant="destructive" onClick={() => onRemover(colaborador)}>
                        remover
                      </Button>
                    )}
                  </div>
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
